/**
 * 🚀 MEDIA ENGINE VERSION: 2026-04-13-STABLE-V5 (Drive Multi-Stage)
 */
console.log("%c🚀 BY HUDSON RAONE - Media Engine V5 Active", "color: #00FF33; font-weight: bold; font-size: 12px;");

/**
 * Compress an image file before upload.
 * Resizes to maxWidth and compresses to target quality.
 */
export const compressImage = (
 file: File,
 maxWidth = 1080,
 quality = 0.75
): Promise<File> => {
 return new Promise((resolve, reject) => {
 if (!file.type.startsWith("image/")) {
 resolve(file); // skip non-images
 return;
 }

 const img = new Image();
 const url = URL.createObjectURL(file);

 // Global timeout for the entire compression process
 const timeout = setTimeout(() => {
    URL.revokeObjectURL(url);
    resolve(file); // safety fallback to prevent hanging
 }, 5000);

 img.onload = () => {
 URL.revokeObjectURL(url);

 let { width, height } = img;
 if (width > maxWidth) {
 height = Math.round((height * maxWidth) / width);
 width = maxWidth;
 }

 const canvas = document.createElement("canvas");
 canvas.width = width;
 canvas.height = height;

 const ctx = canvas.getContext("2d");
 if (!ctx) {
 resolve(file);
 return;
 }
 ctx.drawImage(img, 0, 0, width, height);

  canvas.toBlob(
    (blob) => {
      clearTimeout(timeout);
      if (!blob) {
        resolve(file);
        return;
      }
      const compressed = new File([blob], file.name, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
      resolve(compressed.size < file.size ? compressed : file);
    },
    "image/jpeg",
    quality
  );
 };

 img.onerror = () => {
 clearTimeout(timeout);
 URL.revokeObjectURL(url);
 resolve(file); // fallback to original
 };

 img.src = url;
 });
};


/**
 * Add a watermark image to a photo.
 * watermarkUrl: URL/DataURL of the watermark image.
 * position: "center", "bottom-right", "top-left", etc (currently only center/bottom-right).
 */
export interface WatermarkOptions {
 type: "image" | "text";
 content: string; // URL for image, or the text string
 mode: "center" | "tile" | "fit";
 opacity?: number;
 rotated?: boolean;
}

export const addWatermark = async (file: File, options: WatermarkOptions): Promise<File> => {
 if (!file.type.startsWith("image")) return file;

 return new Promise((resolve) => {
 const img = new Image();
 const url = URL.createObjectURL(file);
 img.src = url;
 
 img.onload = () => {
 const canvas = document.createElement("canvas");
 const ctx = canvas.getContext("2d");
 if (!ctx) {
 URL.revokeObjectURL(url);
 resolve(file);
 return;
 }

 canvas.width = img.width;
 canvas.height = img.height;
 
 // Draw original image
 ctx.drawImage(img, 0, 0);
 URL.revokeObjectURL(url);

 const drawContent = (wImg?: HTMLImageElement) => {
 ctx.globalAlpha = options.opacity !== undefined ? options.opacity : 0.7;
 
 if (options.type === "image" && wImg) {
 if (options.mode === "center") {
 const w = canvas.width * 0.4;
 const h = (wImg.height / wImg.width) * w;
 ctx.drawImage(wImg, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
 } else if (options.mode === "tile") {
          // Grade limpa e alinhada (Com inclinação diagonal de 30º para estética)
          const size = canvas.width * 0.25; // Aumentado de 0.15 para 0.25
          const spacingX = size * 2.0;
          const spacingY = size * 1.5;
          
          // Início com offset para cobrir as bordas
          for (let x = -spacingX; x < canvas.width + spacingX; x += spacingX) {
            for (let y = -spacingY; y < canvas.height + spacingY; y += spacingY) {
              ctx.save();
              ctx.translate(x + spacingX / 2, y + spacingY / 2);
              
              // Rotação: 30º se 'rotated' for true, 0º (horizontal) se false
              if (options.rotated) {
                ctx.rotate(-Math.PI / 6);
              }
              
              const w = size;
              const h = (wImg.height / wImg.width) * w;
              ctx.drawImage(wImg, -w/2, -h/2, w, h);
              ctx.restore();
            }
          }
 } else if (options.mode === "fit") {
          // Uma única logo gigante que cobre grande parte da foto
          ctx.save();
          const w = canvas.width * 0.85; // 85% of width
          const h = (wImg.height / wImg.width) * w;
          
          ctx.translate(canvas.width / 2, canvas.height / 2);
          
          if (options.rotated) {
            ctx.rotate(-Math.PI / 6);
          }
          
          ctx.drawImage(wImg, -w / 2, -h / 2, w, h);
          ctx.restore();
        }
 } else if (options.type === "text") {
 ctx.fillStyle = "white";
 ctx.strokeStyle = "rgba(0,0,0,0.5)";
 ctx.lineWidth = Math.max(1, canvas.width / 500);
 ctx.textAlign = "center";
 
 if (options.mode === "center") {
 const fontSize = Math.floor(canvas.width / 10);
 ctx.font = `bold ${fontSize}px sans-serif`;
 ctx.strokeText(options.content, canvas.width / 2, canvas.height / 2);
 ctx.fillText(options.content, canvas.width / 2, canvas.height / 2);
        } else if (options.mode === "tile") {
          const fontSize = Math.floor(canvas.width / 15); // Aumentado de /25 para /15
          ctx.font = `bold ${fontSize}px sans-serif`;
          
          const textWidth = ctx.measureText(options.content).width + 150;
          const textHeight = fontSize + 200;
          
          // Grade de texto reta e organizada
          for (let x = -textWidth; x < canvas.width + textWidth; x += textWidth) {
            for (let y = -textHeight; y < canvas.height + textHeight; y += textHeight) {
              ctx.save();
              ctx.translate(x + textWidth / 2, y + textHeight / 2);
              
              if (options.rotated) {
                ctx.rotate(-Math.PI / 6); // Mudado de -PI/2 (90º) para -PI/6 (30º) igual a imagem
              }
              
              ctx.shadowColor = "rgba(0,0,0,0.6)";
              ctx.shadowBlur = 6;
              ctx.fillText(options.content, 0, 0);
              ctx.restore();
            }
          }
 } else if (options.mode === "fit") {
 const fontSize = Math.floor(canvas.width / 6);
 ctx.font = `bold ${fontSize}px sans-serif`;
 ctx.strokeText(options.content, canvas.width / 2, canvas.height / 2);
 ctx.fillText(options.content, canvas.width / 2, canvas.height / 2);
 }
 }
 
 // Anti-AI / Adversarial Noise + Forensic Watermark
 // 1. Subtle random noise pattern
 const noiseData = ctx.createImageData(canvas.width, canvas.height);
 const data = noiseData.data;
 for (let i = 0; i < data.length; i += 4) {
 const val = Math.random() * 255;
 data[i] = val;
 data[i+1] = val;
 data[i+2] = val;
 data[i+3] = 4; // Very low opacity (1.5%)
 }
 ctx.putImageData(noiseData, 0, 0);

 // 2. Invisible Forensic text
 ctx.globalAlpha = 0.02;
 ctx.fillStyle = "white";
 ctx.font = `bold ${Math.floor(canvas.width / 30)}px monospace`;
 ctx.textAlign = "center";
 for (let x = -canvas.width; x < canvas.width * 2; x += canvas.width / 3) {
 for (let y = -canvas.height; y < canvas.height * 2; y += canvas.height / 3) {
 ctx.save();
 ctx.translate(x, y);
 ctx.rotate(-Math.PI / 4);
 ctx.fillText("PROTECTED", 0, 0);
 ctx.restore();
 }
 }
 
 // Reset globalAlpha
 ctx.globalAlpha = 1.0;

 canvas.toBlob((blob) => {
 if (blob) {
 resolve(new File([blob], file.name, { type: "image/jpeg" }));
 } else {
 resolve(file);
 }
 }, "image/jpeg", 0.85);
 };

 if (options.type === "image") {
 const wImg = new Image();
 wImg.crossOrigin = "anonymous";
 wImg.src = options.content;
 wImg.onload = () => drawContent(wImg);
 wImg.onerror = () => resolve(file);
 } else {
 drawContent();
 }
 };
 img.onerror = () => {
 URL.revokeObjectURL(url);
 resolve(file);
 };
 });
};

/**
 * Generate a thumbnail from a video file by capturing a frame at 1 second.
 */
export const generateVideoThumbnail = (file: File): Promise<File | null> => {
 return new Promise((resolve) => {
 if (!file.type.startsWith("video/")) {
 resolve(null);
 return;
 }

 const video = document.createElement("video");
 const url = URL.createObjectURL(file);
 video.src = url;
 video.preload = "metadata";
 video.muted = true;
 video.playsInline = true;

 video.onloadedmetadata = () => {
 // Seek to 1 second (or middle if video is very short)
 video.currentTime = Math.min(1, video.duration / 2);
 };

 video.onseeked = () => {
 const canvas = document.createElement("canvas");
 canvas.width = video.videoWidth;
 canvas.height = video.videoHeight;
 const ctx = canvas.getContext("2d");
 if (!ctx) {
 URL.revokeObjectURL(url);
 resolve(null);
 return;
 }
 ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
 URL.revokeObjectURL(url);

 canvas.toBlob((blob) => {
 if (blob) {
 resolve(new File([blob], `${file.name.split(".")[0]}_thumb.jpg`, { type: "image/jpeg" }));
 } else {
 resolve(null);
 }
 }, "image/jpeg", 0.7);
 };

 video.onerror = () => {
 URL.revokeObjectURL(url);
 resolve(null);
 };
 });
};

/**
 * Get cropped image as a File object.
 */
export const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  fileName: string,
  outputSize: number = 512
): Promise<File> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (error) => reject(error));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = outputSize;
  canvas.height = outputSize;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      resolve(new File([blob], fileName, { type: "image/jpeg" }));
    }, "image/jpeg", 0.85);
  });
};

export function swapSupabaseProject(url: string) {
 // ID do projeto NOVO (Produção)
 const NEW_PROJECT = "ietixdawwqppcmxtrtmq";
 // IDs de projetos ANTIGOS ou UUIDs fantasmas
 const GHOST_PROJECT = "s59e62ef-13b8-4489-a3ee-293fe85ecece";
 const TYPO_PROJECT = "ietixdawwgppcmxtrtmg"; // Note the 'g' at the end

 let finalUrl = url;
 if (finalUrl.includes(GHOST_PROJECT)) {
 finalUrl = finalUrl.replace(GHOST_PROJECT, NEW_PROJECT);
 }
 if (finalUrl.includes(TYPO_PROJECT)) {
 finalUrl = finalUrl.replace(TYPO_PROJECT, NEW_PROJECT);
 }
 
 return finalUrl;
}


/**
 * 🚀 MEDIA ENGINE VERSION: 2026-05-05-STABLE-V7 (Pro Recovery)
 */
console.log("%c🚀 BY HUDSON RAONE - Media Engine V7 Active", "color: #FF6A00; font-weight: bold; font-size: 12px;");

/**
 * Central de Inteligência de URLs de Mídia.
 * Prioriza drive.usercontent para melhor performance em galerias.
 */
export function transformDriveUrl(url: string, size: any = 1000) {
 if (!url || typeof url !== 'string' || url.trim() === "") return "";
 
 const numericSize = (typeof size === 'number' && !isNaN(size)) ? Math.round(size) : 1000;
 
 // 1. RESOLUÇÃO DE CAMINHOS RELATIVOS E IDENTIFICAÇÃO DE RAW DRIVE IDS
 let processedUrl = url;
 const currentEnvUrl = import.meta.env.VITE_SUPABASE_URL || "https://ietixdawwqppcmxtrtmq.supabase.co";

 // Se for apenas um ID (sem barra, sem extensão de arquivo, entre 25 a 40 chars), é um Drive ID!
 const isRawDriveId = /^[-\w]{25,40}$/.test(processedUrl) && !processedUrl.includes('.');

 if (!processedUrl.includes('http') && !processedUrl.includes('google') && !isRawDriveId) {
 processedUrl = `${currentEnvUrl}/storage/v1/object/public/media/${processedUrl}`;
 }

 // 2. CORREÇÃO DE PROJETO E CACHE (SUPABASE)
 if (processedUrl.includes('supabase.co')) {
 const alternativeUrl = swapSupabaseProject(processedUrl);
 if (alternativeUrl !== processedUrl) {
 processedUrl = alternativeUrl;
 }
 
 if (!processedUrl.includes('cb=')) {
 const cb = '2026-05-05';
 processedUrl += processedUrl.includes('?') ? `&cb=${cb}` : `?cb=${cb}`;
 }
 return processedUrl;
 }

 try {
 // 3. TRATAMENTO PARA GOOGLE USER CONTENT
 if (processedUrl.includes('googleusercontent.com') && !processedUrl.includes('/d/')) {
 const baseUrl = processedUrl.split('=')[0];
 return `${baseUrl}=w${numericSize}`;
 }

 // 4. TRATAMENTO PARA GOOGLE DRIVE (Extração de ID robusta)
 const isGoogleLink = processedUrl.includes('drive.google.com') || processedUrl.includes('docs.google.com');
 const hasExplicitDriveMarkers = processedUrl.includes('id=') || processedUrl.includes('/d/');
 const isRawId = /^[-\w]{25,40}$/.test(processedUrl) && !processedUrl.includes('.');
 
 if (isGoogleLink || hasExplicitDriveMarkers || isRawId) {
 const driveIdMatch = processedUrl.match(/(?:id=|\/d\/)([-\w]{25,40})/) || processedUrl.match(/[-\w]{25,40}/);
 if (driveIdMatch) {
 const id = driveIdMatch[1] || driveIdMatch[0];
 const cleanId = id.replace('Drive_', '');
 
 if (cleanId.length >= 25 && !cleanId.includes('supabase')) {
 // V8-ULTRA: drive.google.com/thumbnail é o endpoint mais estável para WebViews mobile
 return `https://drive.google.com/thumbnail?id=${cleanId}&sz=w${numericSize}`;
 }
 }
 }
 } catch (e) {
 console.warn("⚠️ Falha ao transformar URL de mídia:", processedUrl, e);
 }

 return processedUrl;
}

/**
 * Centraliza e blinda a recuperação de erros de imagem em todo o site.
 * V8-ULTRA: Hierarquia de Fallback Reforçada e Compatível com Mobile.
 */
export function handleMediaError(
 e: React.SyntheticEvent<HTMLImageElement, Event>,
 item: { filename?: string; file_url?: string; [key: string]: any },
 size: number = 800
) {
 const target = e.target as HTMLImageElement;
 if (target.dataset.fallbackTried === "true" && target.dataset.driveAttempts === "5") return;

 const originalUrl = target.src;
 
 // 1. TENTATIVA DE AUTO-CURA (SUPABASE)
 if (originalUrl.includes('supabase.co')) {
 const alternativeUrl = swapSupabaseProject(originalUrl);
 if (alternativeUrl !== originalUrl) {
 target.src = alternativeUrl;
 target.dataset.fallbackTried = "true";
 return;
 }
 }

 // 2. TENTATIVA DE AUTO-CURA (GOOGLE DRIVE)
 const isGoogleLink = originalUrl.includes('google') || (item.file_url && item.file_url.includes('google')) || (item.thumbnail_url && item.thumbnail_url.includes('google')) || (item.original_file_url && item.original_file_url.includes('google')) || (item.url && item.url.includes('google'));
 const hasExplicitDriveMarkers = originalUrl.includes('id=') || originalUrl.includes('/d/');

 if (isGoogleLink || hasExplicitDriveMarkers) {
 const idMatch = originalUrl.match(/(?:id=|\/d\/)([-\w]{25,40})/) || 
 (item.file_url && item.file_url.match(/(?:id=|\/d\/)([-\w]{25,40})/)) ||
 (item.thumbnail_url && item.thumbnail_url.match(/(?:id=|\/d\/)([-\w]{25,40})/)) ||
 (item.original_file_url && item.original_file_url.match(/(?:id=|\/d\/)([-\w]{25,40})/)) ||
 (item.url && item.url.match(/(?:id=|\/d\/)([-\w]{25,40})/)) ||
 originalUrl.match(/[-\w]{25,40}/);

 if (idMatch) {
 const id = idMatch[1] || idMatch[0];
 const cleanId = id.replace('Drive_', '');
 
 if (cleanId.length >= 25 && !cleanId.includes('supabase') && !/[0-9a-f]{8}-/.test(cleanId)) {
 let attempts = parseInt(target.dataset.driveAttempts || "0");
 const lhIndex = Math.floor(Math.random() * 4) + 3; // 3-6

 attempts++;
 target.dataset.driveAttempts = attempts.toString();

 if (attempts === 1) {
 // Tenta a rota CDN LH
 target.src = `https://lh${lhIndex}.googleusercontent.com/d/${cleanId}=w${size}`;
 } else if (attempts === 2) {
 // Tenta a rota de UserContent (Download view)
 target.src = `https://drive.usercontent.google.com/download?id=${cleanId}&export=view`;
 } else if (attempts === 3) {
 // Tenta a rota direta UC
 target.src = `https://drive.google.com/uc?export=view&id=${cleanId}`;
 } else if (attempts === 4) {
 // Tenta sem limite de tamanho (Original)
 target.src = `https://drive.google.com/thumbnail?id=${cleanId}&sz=s0`;
 } else if (attempts === 5) {
 // Última tentativa de CDN LH alternativa
 target.src = `https://lh3.googleusercontent.com/d/${cleanId}=w${size}`;
 } else {
 console.error(`🚨 Falha Crítica Drive: Verifique as permissões de compartilhamento (deve estar Público / Leitor) do ID ${cleanId}`);
 target.dataset.fallbackTried = "true";
 }
 return;
 }
 }
 }

 target.dataset.fallbackTried = "true";
}

export function isSupabaseUrl(url?: string): boolean {
 if (!url || typeof url !== "string") return false;
 return url.includes("supabase.co") || url.includes("supabase.in") || (!url.includes("http") && !url.includes("google"));
}
